import { z } from 'zod';

import { CustomerSegmentCriteriaSchema } from './customer-segment.js';
import { CampaignChannelSchema } from './campaign.js';
import { I18nStringSchema, ShortIdSchema, TimestampSchema, UidSchema } from './primitives.js';

/**
 * Lifecycle automation "journeys" — the marketing-automation engine
 * (growth-engine strategy, Phase 1). A journey turns the existing
 * feature shelf (segments, vouchers, points, FCM) into an orchestrated
 * sequence: TRIGGER (who/when) → STEPS (spaced nudges) → CHANNEL →
 * INCENTIVE. Definitions live at `tenants/{tid}/journeys/{journeyId}`;
 * per-subject run-state at `tenants/{tid}/journeyEnrollments/{id}`.
 *
 * Reuses (does not duplicate) `CustomerSegmentCriteriaSchema` for
 * targeting, `CampaignChannelSchema` for delivery, `I18nStringSchema`
 * for copy. See ADR (marketing-automation-engine) + docs/architecture/
 * notifications.md.
 */

/**
 * What starts a journey.
 *  - `segment_scan`   — a scheduled tick enrols customers matching the
 *                       criteria (e.g. `inactiveDays` win-back). REUSES
 *                       the exact segment matcher the composer uses.
 *  - `event`          — enrolled off a Firestore event (first paid wash).
 *  - `operator_trial` — enrols the tenant owner at trial-day thresholds.
 */
export const JourneyTriggerSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('segment_scan'),
    criteria: CustomerSegmentCriteriaSchema.describe('Audience filter, resolved by matchesSegment'),
  }),
  z.object({
    kind: z.literal('event'),
    event: z.enum(['first_wash']).describe('The lifecycle event that enrols the subject'),
  }),
  z.object({
    kind: z.literal('operator_trial'),
    daysBeforeTrialEnd: z
      .array(z.number().int().positive())
      .min(1)
      .describe('Trial days-remaining thresholds that enrol the owner, e.g. [7,2,1]'),
  }),
]);
export type JourneyTrigger = z.infer<typeof JourneyTriggerSchema>;

/** Optional reward attached to a step. Mirrors the loyalty reward unions. */
export const JourneyIncentiveSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('none') }),
  z.object({
    kind: z.literal('voucher'),
    voucherTemplateId: ShortIdSchema.describe('Template to issue when this step fires'),
  }),
  z.object({
    kind: z.literal('points'),
    amount: z.number().int().positive().describe('Loyalty points to grant'),
  }),
]);
export type JourneyIncentive = z.infer<typeof JourneyIncentiveSchema>;

/** One nudge in the sequence. `afterDays` is measured from `enrolledAt`. */
export const JourneyStepSchema = z.object({
  afterDays: z
    .number()
    .int()
    .nonnegative()
    .describe('Delay in days from enrolment before this step fires; step 0 is typically 0'),
  channels: z
    .array(CampaignChannelSchema)
    .min(1)
    .describe('Delivery channels for this step (web_push + in_app_banner now; whatsapp later)'),
  content: z
    .object({
      title: I18nStringSchema,
      body: I18nStringSchema,
      deepLink: z.string().max(512).optional().describe('In-app path the nudge opens, e.g. /book'),
    })
    .describe('The localized message body — authored at definition time (no per-send AI cost)'),
  incentive: JourneyIncentiveSchema.default({ kind: 'none' }),
});
export type JourneyStep = z.infer<typeof JourneyStepSchema>;

/** A journey definition (operator- or platform-authored). */
export const JourneySchema = z.object({
  id: ShortIdSchema,
  name: z
    .string()
    .min(1)
    .max(80)
    .describe('Operator-facing label, e.g. "Win back lapsed regulars"'),
  trigger: JourneyTriggerSchema,
  steps: z.array(JourneyStepSchema).min(1).max(6),
  /** Win-back semantics: exit if the customer visits again after enrolling. */
  exitOnReturn: z.boolean().default(true),
  /** Cross-journey weekly cap per subject (enforced on the rateLimits primitive). */
  frequencyCapPerWeek: z.number().int().positive().max(14).default(2),
  active: z.boolean().default(true),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type Journey = z.infer<typeof JourneySchema>;

export const JourneyEnrollmentStatusSchema = z.enum(['active', 'completed', 'exited']);
export type JourneyEnrollmentStatus = z.infer<typeof JourneyEnrollmentStatusSchema>;

/**
 * Enrolment doc id — a URL-safe COMPOSITE (`{journeyId}_{uidTail}_{cohortKey}`),
 * deterministic so re-scans never double-enrol. Wider than `ShortIdSchema`
 * (which caps at 24) because it concatenates three parts; still `[A-Za-z0-9_-]`.
 */
export const JourneyEnrollmentIdSchema = z
  .string()
  .regex(/^[A-Za-z0-9_-]+$/)
  .min(6)
  .max(80)
  .describe('Deterministic composite enrolment id');

/**
 * Per-subject run-state. Flat under the tenant (not nested in the journey)
 * so the runner can query the whole tenant's "due now" set in one indexed
 * query. `cohortKey` + a deterministic id make (re-)enrolment idempotent.
 */
export const JourneyEnrollmentSchema = z.object({
  id: JourneyEnrollmentIdSchema,
  journeyId: ShortIdSchema,
  /** Customer uid, OR the tenant owner uid for an `operator_trial` journey. */
  subjectUid: UidSchema,
  /** Dedupe / re-enrolment key (e.g. base36 epoch-day of the lapse). */
  cohortKey: z.string().min(1).max(16),
  status: JourneyEnrollmentStatusSchema,
  currentStepIndex: z.number().int().nonnegative().default(0),
  enrolledAt: TimestampSchema,
  /** Due time for `currentStepIndex`. The runner queries `nextStepAt <= now`. */
  nextStepAt: TimestampSchema,
  lastSentAt: TimestampSchema.optional(),
  exitReason: z.string().max(64).optional(),
  updatedAt: TimestampSchema,
});
export type JourneyEnrollment = z.infer<typeof JourneyEnrollmentSchema>;

/**
 * The `tenantId` written on an inbox message that belongs to no single operator
 * — an agent's payout, a recruiter's identity check, a super-admin queue item.
 *
 * A sentinel rather than an optional field, so the field stays required and a
 * customer app filtering `tenantId === <this shop>` can never show one of these
 * by accident. Same value the earnings ledger already uses for platform rows.
 */
export const PLATFORM_INBOX_TENANT_ID = '__platform__';

/** Who an inbox message is written for. Lets one uid's inbox serve two roles. */
export const InboxAudienceSchema = z.enum([
  'customer',
  'operator',
  'agent',
  'recruiter',
  'superadmin',
]);
export type InboxAudience = z.infer<typeof InboxAudienceSchema>;

/**
 * In-app inbox message — the reach for the (majority) of users with no FCM
 * token. Written at `users/{uid}/inbox/{msgId}` (and, for super-admins, at the
 * brand-scoped `platformBrands/{brandId}/adminInbox/{msgId}`) by the server only.
 *
 * Rendering contract for the clients:
 *   - `title` / `body` are already resolved to the recipient's locale. When
 *     `titleI18n` / `bodyI18n` are present, prefer `titleI18n[viewerLocale]`
 *     (then `title`) so a shared inbox reads in each viewer's language.
 *   - `deepLink` is an IN-APP ROUTE (`/queue/abc`, `/billing`), relative to the
 *     app's own router basename — never slug-prefixed, never absolute. Push
 *     notifications carry the slug-aware absolute URL separately.
 *   - `tenantId` is the operator the message is about, or
 *     `PLATFORM_INBOX_TENANT_ID`. The customer app shows only its own tenant.
 *   - `audience` (absent on messages written before it existed) says which of a
 *     uid's roles the message is for; the customer app shows `customer` only.
 */
export const InboxMessageSchema = z.object({
  id: ShortIdSchema,
  tenantId: z.string().min(1),
  title: z.string().min(1).max(200),
  body: z.string().min(1).max(1000),
  deepLink: z.string().max(512).optional(),
  /**
   * What produced this message.
   *
   * 'milestone' joined 'marketing' with the fan-out (gap G26): a milestone is
   * delivered through the same inbox an operator already reads, rather than a
   * parallel surface nobody would think to open. 'transactional' is everything
   * that tells a user what just happened to THEIR booking, payment, invoice or
   * payout — never gated by marketing consent, quiet hours or caps.
   */
  kind: z.enum(['marketing', 'milestone', 'transactional']),
  audience: InboxAudienceSchema.optional(),
  source: z.union([
    z.object({
      journeyId: ShortIdSchema,
      stepIndex: z.number().int().nonnegative(),
    }),
    z.object({
      /** The threshold crossed, e.g. 1000 washes. */
      milestone: z.number().int().positive(),
      /** Whose version of the milestone this is. */
      audience: z.enum(['operator', 'agent', 'customer']),
    }),
    z.object({
      /** Transactional event key, e.g. `ticket.readyForCollection`. */
      event: z.string().min(1).max(64),
    }),
    z.object({
      /** A broadcast campaign sent through `runCampaignNow`. */
      campaignId: z.string().min(1).max(64),
    }),
  ]),
  /** All-locale copy, so a reader can render the viewer's own language. */
  titleI18n: I18nStringSchema.optional(),
  bodyI18n: I18nStringSchema.optional(),
  readAt: TimestampSchema.optional(),
  createdAt: TimestampSchema,
});
export type InboxMessage = z.infer<typeof InboxMessageSchema>;
